<template>
  <div class="fields-panel">
    <FieldsOrderFormModal :fields="rows" @order-change="applyFieldOrder" />

    <p v-if="!rows.length" class="fields-empty">
      No hay campos disponibles para configurar en esta capa.
    </p>

    <p v-if="rows.length" class="report-limit-info">
      Puede definir hasta 6 columnas visibles inicialmente en el reporte.
    </p>
    <p v-if="reportLimitWarning" class="report-limit-warning">{{ reportLimitWarning }}</p>

    <table v-if="rows.length" class="fields-table">
      <thead>
        <tr>
          <th>Visible</th>
          <th>Campo</th>
          <th>Alias</th>
          <th>Orden</th>
          <th>Tooltip</th>
          <th>Visible inicialmente en reporte</th>
          <th>Título</th>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="(row, i) in rows"
          :key="i"
          :class="{
            selected: row.visible,
            disabled: !row.visible
          }"
        >
          <td>
            <input type="checkbox" v-model="row.visible" @change="emitUpdate" />
          </td>

          <td class="field-name">{{ row.name }}</td>

          <td>
            <input
              class="input"
              type="text"
              v-model="row.alias"
              placeholder="Alias"
              :disabled="!row.visible"
              @input="emitUpdate"
            />
          </td>

          <td>
            {{ row.order }}
          </td>

          <td><input type="checkbox" v-model="row.tooltip" :disabled="!row.visible" @change="emitUpdate" /></td>
          <td><input type="checkbox" v-model="row.reporte" :disabled="!row.visible" @change="onReportChange(row)" /></td>
          <td><input type="checkbox" v-model="row.titulo" :disabled="!row.visible" @change="emitUpdate" /></td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import FieldsOrderFormModal from "./FieldsOrderFormModal.vue";

export default {
  name: "FormularioArcgisPaso2",
  components: { FieldsOrderFormModal },
  props: {
    value: { 
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      rows: [],
      reportLimitWarning: "",
    };
  },

  created() {
    this.initRows();
  },

  watch: {
    value: {
      handler() {
        // El padre reasigna fieldsArcgis con el MISMO array que emitUpdate le pasó,
        // así que el watch deep se dispara con cada tecla del alias. Sin este guard,
        // initRows() reordenaría la tabla mientras el usuario escribe.
        if (this.value === this.rows) return;
        this.initRows();
      },
      deep: true,
      immediate: true,
    },
  },

  methods: {
    initRows() {
      if (!this.value || this.value.length === 0) {
        this.rows = [];
        this.reportLimitWarning = "";
        return;
      }

      // Se respeta el `order` guardado (capas ya configuradas) y se reasigna 1..N:
      // desde ahora el orden es la posición en la lista, no un número tecleado.
      const normalized = this.value.map((f, index) => {
        if (typeof f === "string") {
          return {
            name: f,
            alias: "",
            order: index + 1,
            visible: true,
            tooltip: false,
            reporte: false,
            titulo: false,
          };
        }
        return {
          name: f.name || "",
          alias: f.alias || "",
          order: f.order || index + 1,
          visible: !!f.visible,
          tooltip: !!f.tooltip,
          reporte: !!f.reporte,
          titulo: !!f.titulo,
        };
      });

      normalized.sort((a, b) => a.order - b.order);
      let seleccionadasReporte = 0;
      let excediaLimite = false;
      this.rows = normalized.map((row, index) => {
        const normalizada = { ...row, order: index + 1 };
        if (normalizada.visible && normalizada.reporte) {
          if (seleccionadasReporte >= 6) {
            normalizada.reporte = false;
            excediaLimite = true;
          } else {
            seleccionadasReporte += 1;
          }
        }
        return normalizada;
      });
      this.reportLimitWarning = excediaLimite
        ? "Existe un límite de 6 columnas; se conservaron las primeras según el orden configurado."
        : "";
    },

    // El modal devuelve solo los nombres en su nuevo orden; las filas vivas
    // (con los alias que el usuario esté editando) son las de this.rows.
    applyFieldOrder(orderedNames) {
      const buckets = new Map();
      for (const row of this.rows) {
        if (!buckets.has(row.name)) buckets.set(row.name, []);
        buckets.get(row.name).push(row);
      }
      const reordered = orderedNames.map((name) => buckets.get(name)?.shift()).filter(Boolean);
      this.rows.forEach((row) => {
        if (!reordered.includes(row)) reordered.push(row);
      });
      reordered.forEach((row, index) => { row.order = index + 1; });
      this.rows = reordered;
      this.emitUpdate();
    },

    onReportChange(row) {
      const seleccionadas = this.rows.filter(field => field.visible && field.reporte).length;
      if (row.reporte && seleccionadas > 6) {
        row.reporte = false;
        this.reportLimitWarning = "Existe un límite de 6 columnas, desactive una ya seleccionada.";
      } else {
        this.reportLimitWarning = "";
      }
      this.emitUpdate();
    },

    emitUpdate() {
        this.$emit('input', this.rows);
    }
  },
};
</script>

<style scoped>
.fields-panel {
  background: #f3f3f3;
  padding: 15px;
  border-radius: 8px;
  max-height: 350px;
  overflow-y: auto;
  border: 1px solid #ddd;
}

.fields-table {
  width: 100%;
  border-collapse: collapse;
}

.fields-table th {
  font-weight: bold;
  padding: 10px;
  text-align: left;
  border-bottom: 1px solid #ccc;
}

.fields-table tbody tr {
  background: #efefef;
  transition: background 0.2s ease, opacity 0.2s ease;
}

.fields-table tbody tr.selected {
  background: #ffffff !important;
  border-left: 3px solid #187bcd;
}

.fields-table tbody tr.disabled {
  opacity: 0.6;
}

.fields-table td {
  padding: 8px;
}

.field-name {
  color: #555;
}

.input {
  width: 100%;
  padding: 6px;
  border: 1px solid #bbb;
  background: #fff;
  border-radius: 4px;
}

.input:disabled {
  background: #e9ecef;
  cursor: not-allowed;
}

.fields-table tbody tr.disabled input[type="checkbox"]:not(:first-child) {
  cursor: not-allowed;
}

.fields-empty {
  margin: 12px 0 0;
  color: #667786;
  font-size: .85rem;
}

.report-limit-info,
.report-limit-warning {
  margin: 8px 0;
  font-size: .78rem;
}

.report-limit-info {
  color: #667786;
}

.report-limit-warning {
  color: #b42318;
  font-weight: 700;
}
</style>
